import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { PremiumCard } from "./premium-card"
import { PremiumBadge } from "./premium-badge"

const premiumMetricsCardVariants = cva(
  "relative overflow-hidden",
  {
    variants: {
      variant: {
        default: "bg-card",
        gradient: "bg-gradient-to-br from-primary/5 via-background to-accent/5",
        success: "bg-gradient-to-br from-success/5 via-background to-success/10",
        warning: "bg-gradient-to-br from-warning/5 via-background to-warning/10",
        accent: "bg-gradient-to-br from-accent/5 via-background to-accent/10",
        glass: "bg-transparent",
      },
      trend: {
        none: "",
        up: "border-l-4 border-success",
        down: "border-l-4 border-destructive",
        flat: "border-l-4 border-neutral-400",
      }
    },
    defaultVariants: {
      variant: "default",
      trend: "none",
    },
  }
)

export interface PremiumMetricsCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof premiumMetricsCardVariants> {
  title: string
  value: string | number
  change?: string
  changeType?: "increase" | "decrease" | "neutral"
  icon?: React.ReactNode
  description?: string
  badge?: {
    text: string
    variant?: "primary" | "accent" | "success" | "warning" | "destructive"
  }
}

const PremiumMetricsCard = React.forwardRef<HTMLDivElement, PremiumMetricsCardProps>(
  ({ 
    className, 
    variant, 
    trend, 
    title, 
    value, 
    change, 
    changeType, 
    icon, 
    description, 
    badge,
    ...props 
  }, ref) => {
    const getChangeColor = () => {
      switch (changeType) {
        case "increase":
          return "text-success"
        case "decrease":
          return "text-destructive"
        default:
          return "text-muted-foreground"
      }
    }

    const getChangeIcon = () => {
      switch (changeType) {
        case "increase":
          return "↗"
        case "decrease":
          return "↘"
        default:
          return "→"
      }
    }

    return (
      <PremiumCard
        ref={ref}
        variant="elevated"
        className={cn(premiumMetricsCardVariants({ variant, trend, className }))}
        {...props}
      >
        {/* Background Decoration */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-50" />
        
        {/* Card Header */}
        <div className="relative flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {icon && (
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary">
                {icon}
              </div>
            )}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
              {description && (
                <p className="text-xs text-muted-foreground/70 mt-1">{description}</p>
              )}
            </div>
          </div>
          
          {badge && (
            <PremiumBadge variant={badge.variant || "primary"} size="sm">
              {badge.text}
            </PremiumBadge>
          )}
        </div>
        
        {/* Main Value */}
        <div className="relative">
          <div className="text-2xl font-bold text-foreground mb-2">
            {value}
          </div>
          
          {/* Change Indicator */}
          {change && (
            <div className={cn("flex items-center gap-1 text-sm font-medium", getChangeColor())}>
              <span className="text-base">{getChangeIcon()}</span>
              <span>{change}</span>
            </div>
          )}
        </div>
        
        {/* Sparkle Effect */}
        <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-primary/20 animate-pulse" />
        <div className="absolute bottom-4 left-4 w-1 h-1 rounded-full bg-accent/30 animate-pulse" 
             style={{ animationDelay: '0.5s' }} />
      </PremiumCard>
    )
  }
)
PremiumMetricsCard.displayName = "PremiumMetricsCard"

export { PremiumMetricsCard, premiumMetricsCardVariants } 